import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ShieldCheck, BadgeCheck, Building2 } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { PHONE_TEL } from "./data";

const HERO_IMG =
  "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxlbGVjdHJpY2lhbiUyMHdvcmtpbmd8ZW58MHx8fHwxNzg3NTAyODI1fDA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const trustItems = [
  { icon: ShieldCheck, label: "Fully insured" },
  { icon: BadgeCheck, label: "Professional service" },
  { icon: Building2, label: "Domestic & commercial" },
];

const Hero = () => {
  const imgWrap = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgWrap,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <section id="home" data-testid="hero-section" className="relative overflow-hidden bg-[#FAFAFA]">
      <div className="absolute top-0 right-0 w-1/3 h-full bg-slate-100 hidden lg:block" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-28 md:pt-36 pb-14 md:pb-24 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600 mb-6"
            data-testid="hero-overline"
          >
            Essex Spark Electrical — Basildon
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.05] text-slate-900"
          >
            <MaskedLine delay={0.15}>Reliable Electrical</MaskedLine>
            <MaskedLine delay={0.28}>Services Across</MaskedLine>
            <MaskedLine delay={0.41}>
              <span className="text-blue-600">Basildon &amp; Essex</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 text-base md:text-lg leading-relaxed text-slate-600 max-w-xl"
            data-testid="hero-subtext"
          >
            Professional electrical work for homes, landlords and local
            businesses. From small electrical jobs to complete installations.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              data-testid="hero-call-button"
              href={PHONE_TEL}
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
            >
              <Phone className="w-4 h-4" /> Call Now
            </a>
            <button
              data-testid="hero-quote-button"
              onClick={() => scrollToId("contact")}
              className="inline-flex items-center justify-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-900 font-bold px-8 py-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
            >
              Request a Quote
            </button>
          </motion.div>
          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
            data-testid="hero-trust-row"
          >
            {trustItems.map((t) => (
              <li key={t.label} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <t.icon className="w-4 h-4 text-amber-500" />
                {t.label}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          ref={imgWrap}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -top-5 -right-5 w-full h-full rounded-tl-[4rem] rounded-br-[4rem] bg-amber-400 hidden sm:block" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-tl-[4rem] rounded-br-[4rem] aspect-[4/5] max-h-[560px] w-full">
            <motion.img
              src={HERO_IMG}
              alt="Electrician in a yellow hard hat installing electrical wiring"
              data-testid="hero-image"
              style={{ y }}
              className="w-full h-full object-cover scale-110"
            />
          </div>
          <div
            data-testid="hero-badge-card"
            className="absolute -bottom-6 -left-4 sm:-left-8 bg-white border border-slate-200 shadow-xl rounded-md px-6 py-4"
          >
            <p className="font-heading text-2xl font-bold text-slate-900 leading-none">Local &amp; reliable</p>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mt-1.5">
              Basildon based electrician
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
